import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Package as PackageIcon, ShoppingBag, Star, AlertTriangle, Tag, Upload, FolderTree, Mail, Copy, Sparkles, Check } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { symbolFor } from "@/utils/priceIntervals";
import { TiptapEditor } from "@/components/admin/TiptapEditor";
import useAutoSave from "@/hooks/useAutoSave";
import { AutoSaveStatus } from "@/components/ui/AutoSaveStatus";
import { pollAiJob } from "@/utils/pollAiJob";
import { ProductBadgePreview } from "@/components/ProductBadge";
import EditOrderDialog from "@/components/orders/EditOrderDialog";

const API = getApiUrl();

const EMPTY = {
  name: "", short_description: "", description: "", price: 0, price_monthly: 0, price_annual: 0, was_price: 0, currency: "GBP",
  image_url: "", image_size: "medium", image_height: 260, image_fit: "contain",
  supplier_id: "", category_ids: [], list_order: "medium",
  featured: false, enabled: true,
  // Image badge overlay defaults — badge_text empty = badge hidden.
  badge_text: "", badge_position: "top-right",
  badge_bg_color: "#ef4444", badge_text_color: "#ffffff",
  badge_font_family: "", badge_font_size: 0, badge_rotation: 0,
  job_description: "",   // "Become our preferred supplier" pitch for this product
  checkout_on_add: false, // skip the basket — jump straight to checkout once added
  allow_add_to_basket: true, // per-product override to hide the Add to Basket CTA
  expires_in_months: 0, // 0 = never expires — months after purchase the entitlement lapses
};

const EMPTY_CODE = {
  code: "", kind: "percent", value: 10,
  min_subtotal: 0, max_uses: 0, enabled: true,
};

const EMPTY_CATEGORY = { name: "", display_order: 0 };

export default function ProductsAdmin() {
  const [tab, setTab] = useState("products");   // "products" | "orders" | "codes"
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [codes, setCodes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Product dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [jobDescNotes, setJobDescNotes] = useState("");
  const [generatingJobDesc, setGeneratingJobDesc] = useState(false);

  // Discount code dialog
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [codeForm, setCodeForm] = useState({ ...EMPTY_CODE });
  const [savingCode, setSavingCode] = useState(false);

  // Category dialog
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ ...EMPTY_CATEGORY });
  const [savingCategory, setSavingCategory] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(null);   // order_id currently being sent
  const [editingOrder, setEditingOrder] = useState(null);       // order object currently open in EditOrderDialog

  const fetchAll = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [p, o, c, s, cats] = await Promise.all([
        axios.get(`${API}/admin/products`, { withCredentials: true }),
        axios.get(`${API}/admin/orders`, { withCredentials: true }),
        axios.get(`${API}/admin/discount-codes`, { withCredentials: true }),
        axios.get(`${API}/admin/suppliers`, { withCredentials: true }).catch(() => ({ data: { suppliers: [] } })),
        axios.get(`${API}/admin/product-categories`, { withCredentials: true }).catch(() => ({ data: { categories: [] } })),
      ]);
      setProducts(p.data.products || []);
      setOrders(o.data.orders || []);
      setCodes(c.data.codes || []);
      setSuppliers(s.data.suppliers || []);
      setCategories(cats.data.categories || []);
    } catch {
      toast.error("Failed to load products/orders/codes");
    } finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Sort products by list_order: High → Medium → Low, then by name for stability
  const LIST_ORDER_WEIGHT = { high: 0, medium: 1, low: 2 };
  const sortedProducts = useMemo(() => {
    const arr = [...products];
    arr.sort((a, b) => {
      const wa = LIST_ORDER_WEIGHT[(a.list_order || "medium").toLowerCase()] ?? 1;
      const wb = LIST_ORDER_WEIGHT[(b.list_order || "medium").toLowerCase()] ?? 1;
      if (wa !== wb) return wa - wb;
      return (a.name || "").localeCompare(b.name || "");
    });
    return arr;
  }, [products]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setShowDialog(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      short_description: p.short_description || "",
      description: p.description || "",
      price: p.price || 0, price_monthly: p.price_monthly || 0, price_annual: p.price_annual || 0, was_price: p.was_price || 0, currency: p.currency || "GBP",
      image_url: p.image_url || "",
      image_size: p.image_size || "medium",
      image_height: p.image_height || 260,
      image_fit: p.image_fit || "contain",
      supplier_id: p.supplier_id || "",
      category_ids: Array.isArray(p.category_ids) ? p.category_ids : (p.category_id ? [p.category_id] : []),
      list_order: p.list_order || "medium",
      featured: p.featured === true,
      enabled: p.enabled !== false,
      badge_text: p.badge_text || "",
      badge_position: p.badge_position || "top-right",
      badge_bg_color: p.badge_bg_color || "#ef4444",
      badge_text_color: p.badge_text_color || "#ffffff",
      badge_font_family: p.badge_font_family || "",
      badge_font_size: p.badge_font_size ?? 0,
      badge_rotation: p.badge_rotation ?? 0,
      job_description: p.job_description || "",
      checkout_on_add: p.checkout_on_add === true,
      allow_add_to_basket: p.allow_add_to_basket !== false,
      expires_in_months: p.expires_in_months ?? 0,
    });
    setShowDialog(true);
  };

  // Duplicate a product via the backend endpoint. Backend returns the new
  // (disabled) copy — we refresh the list and open the edit dialog so the
  // admin can immediately customise the clone before publishing.
  const duplicate = async (p) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/products/${p.product_id}/duplicate`,
        {},
        { withCredentials: true },
      );
      toast.success(`Duplicated as "${data.name}"`);
      await fetchAll({ silent: true });
      openEdit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const toggleFeatured = async (p) => {
    try {
      // Optimistic UI: flip locally, roll back on error.
      setProducts((prev) => prev.map((x) => x.product_id === p.product_id ? { ...x, featured: !p.featured } : x));
      // Send the FULL product payload (not a partial subset) — PUT replaces
      // the whole document via ProductWrite, so any omitted field would
      // silently reset to its default (this previously wiped badge/
      // category/job_description on every star-toggle; now fixed).
      await axios.put(`${API}/admin/products/${p.product_id}`, {
        name: p.name, short_description: p.short_description || "", description: p.description || "",
        price: p.price || 0, price_monthly: p.price_monthly || 0, price_annual: p.price_annual || 0,
        was_price: p.was_price || 0, currency: p.currency || "GBP",
        image_url: p.image_url || "", image_size: p.image_size || "medium",
        image_height: p.image_height || 0, image_fit: p.image_fit || "contain",
        supplier_id: p.supplier_id || "", category_ids: Array.isArray(p.category_ids) ? p.category_ids : (p.category_id ? [p.category_id] : []), list_order: p.list_order || "medium",
        featured: !p.featured,
        enabled: p.enabled !== false,
        badge_text: p.badge_text || "", badge_position: p.badge_position || "top-right",
        badge_bg_color: p.badge_bg_color || "#ef4444", badge_text_color: p.badge_text_color || "#ffffff",
        badge_font_family: p.badge_font_family || "", badge_font_size: p.badge_font_size ?? 0, badge_rotation: p.badge_rotation ?? 0,
        job_description: p.job_description || "",
        checkout_on_add: p.checkout_on_add === true,
        allow_add_to_basket: p.allow_add_to_basket !== false,
        expires_in_months: p.expires_in_months ?? 0,
      }, { withCredentials: true });
      fetchAll();
    } catch {
      toast.error("Could not toggle featured");
      fetchAll();
    }
  };

  // Upload a picked file and set the returned URL on the current form.
  const [uploadingImage, setUploadingImage] = useState(false);
  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingImage(false);
      // Reset the input so the same file can be picked again after clearing.
      e.target.value = "";
    }
  };

  const openCreateCode = () => { setEditingCode(null); setCodeForm({ ...EMPTY_CODE }); setShowCodeDialog(true); };
  const openEditCode = (c) => {
    setEditingCode(c);
    setCodeForm({
      code: c.code, kind: c.kind || "percent", value: c.value,
      min_subtotal: c.min_subtotal || 0, max_uses: c.max_uses || 0,
      enabled: c.enabled !== false,
    });
    setShowCodeDialog(true);
  };
  const saveCode = async () => {
    if (!codeForm.code.trim()) { toast.error("Code is required"); return; }
    if (!(codeForm.value > 0)) { toast.error("Value must be positive"); return; }
    try {
      setSavingCode(true);
      const body = {
        code: codeForm.code.trim().toUpperCase(),
        kind: codeForm.kind, value: Number(codeForm.value),
        min_subtotal: Number(codeForm.min_subtotal || 0),
        max_uses: Number(codeForm.max_uses || 0),
        enabled: codeForm.enabled,
      };
      if (editingCode) {
        await axios.put(`${API}/admin/discount-codes/${editingCode.code_id}`, body, { withCredentials: true });
        toast.success("Code updated");
      } else {
        await axios.post(`${API}/admin/discount-codes`, body, { withCredentials: true });
        toast.success("Code created");
      }
      setShowCodeDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingCode(false); }
  };
  const removeCode = async (c) => {
    if (!confirm(`Delete code "${c.code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${c.code_id}`, { withCredentials: true });
      toast.success("Deleted");
      fetchAll();
    } catch { toast.error("Delete failed"); }
  };

  // --- Categories --------------------------------------------------------
  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ ...EMPTY_CATEGORY });
    setShowCategoryDialog(true);
  };
  const openEditCategory = (c) => {
    setEditingCategory(c);
    setCategoryForm({ name: c.name || "", display_order: c.display_order ?? 0 });
    setShowCategoryDialog(true);
  };
  const saveCategory = async () => {
    if (!categoryForm.name.trim()) { toast.error("Name is required"); return; }
    try {
      setSavingCategory(true);
      const body = {
        name: categoryForm.name.trim(),
        display_order: Number(categoryForm.display_order || 0),
      };
      if (editingCategory) {
        await axios.put(`${API}/admin/product-categories/${editingCategory.category_id}`, body, { withCredentials: true });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/admin/product-categories`, body, { withCredentials: true });
        toast.success("Category created");
      }
      setShowCategoryDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingCategory(false); }
  };
  const removeCategory = async (c) => {
    if (!confirm(`Delete category "${c.name}"? Products in it will be moved to Uncategorised.`)) return;
    try {
      await axios.delete(`${API}/admin/product-categories/${c.category_id}`, { withCredentials: true });
      toast.success("Deleted");
      fetchAll();
    } catch { toast.error("Delete failed"); }
  };

  // --- Orders — resend the branded receipt email ------------------------
  const sendReceipt = async (o) => {
    if (!o.customer?.email) { toast.error("Order has no customer email"); return; }
    try {
      setSendingReceipt(o.order_id);
      await axios.post(`${API}/admin/orders/${o.order_id}/send-receipt`, {}, { withCredentials: true });
      toast.success(`Receipt sent to ${o.customer.email}`);
      fetchAll({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSendingReceipt(null); }
  };

  // --- Orders — amend status via inline dropdown -------------------------
  const updateStatus = async (order_id, next) => {
    // Optimistic UI: reflect the new status straight away, then reconcile
    // with the server (or roll back on failure).
    const prev = orders;
    setOrders((os) => os.map((o) => (o.order_id === order_id ? { ...o, payment_status: next } : o)));
    try {
      await axios.put(`${API}/admin/orders/${order_id}/status`, { status: next }, { withCredentials: true });
      toast.success(`Status updated to ${next}`);
    } catch (e) {
      setOrders(prev);
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  // --- Orders — quick delete (no dialog), used by the row's Delete button
  const deleteOrder = async (o) => {
    if (!window.confirm("Delete this entire order? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/orders/${o.order_id}`, { withCredentials: true });
      toast.success("Order deleted");
      setOrders((os) => os.filter((row) => row.order_id !== o.order_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete order");
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      setSaving(true);
      const body = { ...form, price: Number(form.price || 0), price_monthly: Number(form.price_monthly || 0), price_annual: Number(form.price_annual || 0), was_price: Number(form.was_price || 0), featured: !!form.featured, image_size: form.image_size || "medium", image_height: Number(form.image_height || 0), image_fit: form.image_fit || "contain", expires_in_months: Number(form.expires_in_months || 0) };
      if (editing) {
        await axios.put(`${API}/admin/products/${editing.product_id}`, body, { withCredentials: true });
      } else {
        const { data } = await axios.post(`${API}/admin/products`, body, { withCredentials: true });
        // After the initial create, transition into edit mode so subsequent
        // autosaves target the freshly-minted product_id.
        setEditing(data);
      }
      // Silent refresh so the autosave doesn't unmount the dialog behind
      // the loader spinner (screen-flash on every keystroke debounce).
      fetchAll({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      throw e;
    } finally { setSaving(false); }
  };

  // Autosave the product dialog on every debounced form change once the
  // product has been created (name + non-negative price are the only server
  // requirements). Suppress firing when the dialog isn't visible.
  const autosave = useAutoSave(form, save, {
    delay: 1200,
    enabled: showDialog,
    canSave: (v) => Boolean(v?.name?.trim()) && Number(v?.price) >= 0,
  });

  const remove = async (p) => {
    if (!confirm(`Delete "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.product_id}`, { withCredentials: true });
      toast.success("Deleted");
      fetchAll();
    } catch { toast.error("Delete failed"); }
  };

  const generateJobDescription = async () => {
    if (!editing?.product_id) {
      toast.error("Save the product first, then generate its job description");
      return;
    }
    setGeneratingJobDesc(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/products/${editing.product_id}/generate-job-description`,
        { extra_notes: jobDescNotes },
        { withCredentials: true },
      );
      const result = await pollAiJob(data.job_id);
      const html = (result?.html || "").trim();
      if (!html) {
        toast.error("AI returned no content");
        return;
      }
      setForm((f) => ({ ...f, job_description: html }));
      toast.success("Job description generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI generation failed");
    } finally {
      setGeneratingJobDesc(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="products-admin">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><PackageIcon className="h-6 w-6" /> Products</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage the shop catalogue and view customer orders.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-border rounded overflow-x-auto text-sm max-w-full" role="tablist">
            <button type="button" onClick={() => setTab("products")}
              className={`px-3 py-1.5 font-medium transition-colors ${tab === "products" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="products-tab-products">Catalog ({products.length})</button>
            <button type="button" onClick={() => setTab("orders")}
              className={`px-3 py-1.5 font-medium transition-colors ${tab === "orders" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="products-tab-orders">Orders ({orders.length})</button>
            <button type="button" onClick={() => setTab("codes")}
              className={`px-3 py-1.5 font-medium transition-colors ${tab === "codes" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="products-tab-codes">Discount Codes ({codes.length})</button>
            <button type="button" onClick={() => setTab("categories")}
              className={`px-3 py-1.5 font-medium transition-colors ${tab === "categories" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="products-tab-categories">Categories ({categories.length})</button>
          </div>
          {tab === "products" && (
            <Button onClick={openCreate} data-testid="products-add-btn"><Plus className="h-4 w-4 mr-1" />Add Product</Button>
          )}
          {tab === "codes" && (
            <Button onClick={openCreateCode} data-testid="codes-add-btn"><Plus className="h-4 w-4 mr-1" />Add Code</Button>
          )}
          {tab === "categories" && (
            <Button onClick={openCreateCategory} data-testid="categories-add-btn"><Plus className="h-4 w-4 mr-1" />Add Category</Button>
          )}
        </div>
      </div>

      {tab === "products" && (
        <div className="border border-border rounded overflow-x-auto bg-background">
          {products.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No products yet — click <strong>Add Product</strong> to create your first one.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="p-3 w-8"></th>
                  <th className="p-3">Image</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Price</th>
                  <th className="p-3">List order</th>
                  <th className="p-3 text-center">Description</th>
                  <th className="p-3 text-center">Job desc.</th>
                  <th className="p-3">Supplier</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProducts.map((p) => (
                  <tr
                    key={p.product_id}
                    className="border-t border-border cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => openEdit(p)}
                    data-testid={`product-row-${p.product_id}`}
                  >
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => toggleFeatured(p)}
                        title={p.featured ? "Unpin from top" : "Pin to top"}
                        className={`transition-colors ${p.featured ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`}
                        data-testid={`product-featured-${p.product_id}`}
                      >
                        <Star className={`h-4 w-4 ${p.featured ? "fill-amber-500" : ""}`} />
                      </button>
                    </td>
                    <td className="p-3">
                      {p.image_url ? <img src={p.image_url} alt={p.name} className="h-10 w-10 object-cover rounded" /> : <div className="h-10 w-10 bg-muted rounded" />}
                    </td>
                    <td className="p-3 font-medium">{p.name}</td>
                    <td className="p-3" data-testid={`product-pricing-${p.product_id}`}>
                      <div className="flex flex-col gap-0.5 text-xs whitespace-nowrap">
                        {Number(p.price) > 0 && <span>{symbolFor(p.currency)}{Number(p.price).toFixed(2)} one-off</span>}
                        {Number(p.price_monthly) > 0 && <span>{symbolFor(p.currency)}{Number(p.price_monthly).toFixed(2)}/mo</span>}
                        {Number(p.price_annual) > 0 && <span>{symbolFor(p.currency)}{Number(p.price_annual).toFixed(2)}/yr</span>}
                        {!(Number(p.price) > 0 || Number(p.price_monthly) > 0 || Number(p.price_annual) > 0) && <span className="text-muted-foreground">Free</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      {(() => {
                        const lo = (p.list_order || "medium").toLowerCase();
                        const styles = {
                          high: "bg-emerald-100 text-emerald-700",
                          medium: "bg-slate-100 text-slate-700",
                          low: "bg-amber-100 text-amber-700",
                        };
                        return (
                          <span
                            className={`text-xs px-2 py-0.5 rounded capitalize ${styles[lo] || styles.medium}`}
                            data-testid={`product-list-order-${p.product_id}`}
                          >
                            {lo}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-3 text-center" data-testid={`product-has-description-${p.product_id}`}>
                      {p.description
                        ? <Check className="h-4 w-4 text-emerald-600 inline-block" />
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3 text-center" data-testid={`product-has-job-description-${p.product_id}`}>
                      {p.job_description
                        ? <Check className="h-4 w-4 text-emerald-600 inline-block" />
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {p.supplier_id
                        ? (suppliers.find((s) => s.supplier_id === p.supplier_id)?.name || <span className="italic text-xs">unknown</span>)
                        : <span className="text-xs">Company</span>}
                    </td>
                    <td className="p-3 text-muted-foreground" data-testid={`product-category-${p.product_id}`}>
                      {(Array.isArray(p.category_ids) ? p.category_ids : (p.category_id ? [p.category_id] : [])).length > 0
                        ? (Array.isArray(p.category_ids) ? p.category_ids : [p.category_id])
                            .map((cid) => categories.find((c) => c.category_id === cid)?.name || "unknown")
                            .join(", ")
                        : <span className="text-xs">—</span>}
                    </td>
                    <td className="p-3">
                      {p.enabled === false
                        ? <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">Draft</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Live</span>}
                    </td>
                    <td className="p-3 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`product-edit-${p.product_id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => duplicate(p)} title="Duplicate product" data-testid={`product-duplicate-${p.product_id}`}><Copy className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(p)} data-testid={`product-delete-${p.product_id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-border rounded overflow-x-auto bg-background">
          {orders.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No orders yet — this list fills up once customers complete checkout.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="p-3">Placed</th>
                  <th className="p-3">Customer</th>
                  <th className="p-3">Property</th>
                  <th className="p-3">Items</th>
                  <th className="p-3">Total</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Receipt</th>
                  <th className="p-3 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const prop = o.assigned_property;
                  const statusStyles = {
                    paid: "bg-green-100 text-green-700 border-green-200",
                    pending: "bg-amber-100 text-amber-700 border-amber-200",
                    failed: "bg-red-100 text-red-700 border-red-200",
                    refunded: "bg-blue-100 text-blue-700 border-blue-200",
                    cancelled: "bg-gray-200 text-gray-700 border-gray-300",
                    test: "bg-purple-100 text-purple-700 border-purple-200",
                    void: "bg-slate-200 text-slate-600 border-slate-300",
                  }[o.payment_status] || "bg-muted text-foreground border-border";
                  return (
                  <tr key={o.order_id} className="border-t border-border align-top">
                    <td className="p-3 text-xs whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-3">
                      <div className="font-medium">{o.customer?.name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{o.customer?.email || ""} {o.customer?.phone && ` · ${o.customer.phone}`}</div>
                    </td>
                    <td className="p-3">
                      {prop ? (
                        <Link
                          to={`/property/${prop.property_id}`}
                          className="flex items-center gap-2 group"
                          data-testid={`order-property-${o.order_id}`}
                          title={prop.title}
                        >
                          {prop.main_image ? (
                            <img
                              src={prop.main_image}
                              alt=""
                              className="h-10 w-14 object-cover rounded border border-border shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-10 w-14 bg-muted border border-border rounded shrink-0" />
                          )}
                          <span className="text-xs font-medium text-primary group-hover:underline line-clamp-2 max-w-[180px]">
                            {prop.title || "Untitled property"}
                          </span>
                        </Link>
                      ) : o.is_plan ? (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700"
                          data-testid={`order-plan-badge-${o.order_id}`}
                          title="Account-level agent plan subscription — not tied to a property"
                        >
                          Plan purchase
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {(o.items || []).map((it, idx) => (
                        <div key={idx} className="text-xs">{it.quantity} × {it.name}</div>
                      ))}
                    </td>
                    <td className="p-3 whitespace-nowrap">{o.currency} {Number(o.subtotal || 0).toFixed(2)}</td>
                    <td className="p-3">
                      <select
                        value={o.payment_status || "pending"}
                        onChange={(e) => updateStatus(o.order_id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded border cursor-pointer ${statusStyles}`}
                        data-testid={`order-status-select-${o.order_id}`}
                        aria-label="Order status"
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="test">Test</option>
                        <option value="void">Void</option>
                      </select>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {o.receipt_sent ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 mr-2" title={o.receipt_sent_at ? `Sent ${new Date(o.receipt_sent_at).toLocaleString()}` : "Sent"}>
                          Sent
                        </span>
                      ) : null}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!o.customer?.email || sendingReceipt === o.order_id}
                        onClick={() => sendReceipt(o)}
                        data-testid={`order-send-receipt-${o.order_id}`}
                        title={o.customer?.email ? "Re-send the branded receipt to the customer" : "Order has no customer email"}
                      >
                        {sendingReceipt === o.order_id
                          ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Sending…</>
                          : <><Mail className="mr-1 h-3 w-3" />{o.receipt_sent ? "Resend" : "Send"}</>}
                      </Button>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="mr-2"
                        onClick={() => setEditingOrder(o)}
                        data-testid={`order-manage-btn-${o.order_id}`}
                        title="Amend items, quantities or prices on this order"
                      >
                        <Pencil className="mr-1 h-3 w-3" />Manage
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => deleteOrder(o)}
                        data-testid={`order-delete-btn-${o.order_id}`}
                        title="Delete this order"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "codes" && (
        <div className="border border-border rounded overflow-x-auto bg-background" data-testid="codes-table">
          {codes.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No discount codes yet — click <strong>Add Code</strong> to create one.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="p-3">Code</th>
                  <th className="p-3">Discount</th>
                  <th className="p-3">Min subtotal</th>
                  <th className="p-3">Usage</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code_id} className="border-t border-border" data-testid={`code-row-${c.code_id}`}>
                    <td className="p-3 font-mono font-semibold flex items-center gap-2"><Tag className="h-3 w-3" />{c.code}</td>
                    <td className="p-3">
                      {c.kind === "percent" ? `${c.value}% off` : `£${Number(c.value).toFixed(2)} off`}
                    </td>
                    <td className="p-3">{c.min_subtotal ? `£${Number(c.min_subtotal).toFixed(2)}` : "—"}</td>
                    <td className="p-3">{c.uses_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                    <td className="p-3">
                      {c.enabled === false
                        ? <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">Disabled</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Active</span>}
                    </td>
                    <td className="p-3 text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditCode(c)} data-testid={`code-edit-${c.code_id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => removeCode(c)} data-testid={`code-delete-${c.code_id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "categories" && (
        <div className="border border-border rounded overflow-x-auto bg-background" data-testid="categories-table">
          {categories.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No categories yet — click <strong>Add Category</strong> to group your products.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Display order</th>
                  <th className="p-3">Products</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => {
                  const count = products.filter((p) => (Array.isArray(p.category_ids) ? p.category_ids : (p.category_id ? [p.category_id] : [])).includes(c.category_id)).length;
                  return (
                    <tr key={c.category_id} className="border-t border-border" data-testid={`category-row-${c.category_id}`}>
                      <td className="p-3 font-medium flex items-center gap-2"><FolderTree className="h-3 w-3" />{c.name}</td>
                      <td className="p-3">{c.display_order}</td>
                      <td className="p-3 text-muted-foreground">{count}</td>
                      <td className="p-3 text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditCategory(c)} data-testid={`category-edit-${c.category_id}`}><Pencil className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => removeCategory(c)} data-testid={`category-delete-${c.category_id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-3 w-3" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Dialog open={showCodeDialog} onOpenChange={setShowCodeDialog}>
        <DialogContent className="max-w-md" data-testid="code-dialog">
          <DialogHeader>
            <DialogTitle>{editingCode ? "Edit Code" : "New Discount Code"}</DialogTitle>
            <DialogDescription>Codes are case-insensitive. Customers enter them at checkout.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code *</Label>
              <Input value={codeForm.code} onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value.toUpperCase() })} className="font-mono uppercase" data-testid="code-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount type</Label>
                <select className="w-full h-10 px-3 border border-input rounded" value={codeForm.kind} onChange={(e) => setCodeForm({ ...codeForm, kind: e.target.value })} data-testid="code-kind">
                  <option value="percent">Percent off</option>
                  <option value="fixed">Fixed amount off</option>
                </select>
              </div>
              <div>
                <Label>Value * {codeForm.kind === "percent" ? "(%)" : "(£)"}</Label>
                <Input type="number" min="0" step="0.01" value={codeForm.value} onChange={(e) => setCodeForm({ ...codeForm, value: e.target.value })} data-testid="code-value" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min subtotal (£)</Label>
                <Input type="number" min="0" step="0.01" value={codeForm.min_subtotal} onChange={(e) => setCodeForm({ ...codeForm, min_subtotal: e.target.value })} data-testid="code-min" />
              </div>
              <div>
                <Label>Max uses (0 = unlimited)</Label>
                <Input type="number" min="0" value={codeForm.max_uses} onChange={(e) => setCodeForm({ ...codeForm, max_uses: e.target.value })} data-testid="code-max" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={codeForm.enabled} onCheckedChange={(v) => setCodeForm({ ...codeForm, enabled: v })} data-testid="code-enabled" />
              <Label className="mb-0">Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCodeDialog(false)}>Cancel</Button>
            <Button onClick={saveCode} disabled={savingCode} data-testid="code-save">
              {savingCode ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : (editingCode ? "Update" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Category" : "New Category"}</DialogTitle>
            <DialogDescription>Group products under a category. Lower display order values appear first.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                data-testid="category-name"
              />
            </div>
            <div>
              <Label>Display order</Label>
              <Input
                type="number"
                step="1"
                value={categoryForm.display_order}
                onChange={(e) => setCategoryForm({ ...categoryForm, display_order: e.target.value })}
                data-testid="category-display-order"
              />
              <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first on the shop grid.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={saveCategory} disabled={savingCategory} data-testid="category-save">
              {savingCategory ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : (editingCategory ? "Update" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-4">
              <span>{editing ? "Edit Product" : "Add Product"}</span>
              <AutoSaveStatus status={autosave.status} />
            </DialogTitle>
            <DialogDescription>Products with <em>Live</em> status appear in the shop. Changes save automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" />
            </div>
            {editing?.product_id && (
              <div>
                <Label>Deep link (website product page)</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/products-all?product=${editing.product_id}`}
                    className="text-xs text-muted-foreground"
                    data-testid="product-deep-link-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/products-all?product=${editing.product_id}`);
                      toast.success("Deep link copied");
                    }}
                    data-testid="product-deep-link-copy"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Opens the shop with this product's detail view — handy for campaign links and promos.
                </p>
              </div>
            )}
            <div>
              <Label>Short description</Label>
              <Input
                value={form.short_description}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                placeholder="One-line summary shown under the name on the shop grid"
                maxLength={140}
                data-testid="product-short-desc"
              />
            </div>
            <div>
              <Label>Description</Label>
              <div className="[&_.ProseMirror]:min-h-[140px] [&_.ProseMirror]:max-h-[220px] [&_.ProseMirror]:overflow-y-auto">
                <TiptapEditor
                  value={form.description}
                  onChange={(html) => setForm({ ...form, description: html })}
                  placeholder="Full product details — use the toolbar for formatting."
                />
              </div>
            </div>
            <div className="space-y-2 rounded-md border border-primary/30 bg-primary/[0.03] p-3">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Job description
                </Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={generateJobDescription}
                  disabled={generatingJobDesc || !editing?.product_id}
                  className="gap-1.5"
                  data-testid="product-generate-job-description"
                >
                  {generatingJobDesc ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  Generate with AI
                </Button>
              </div>
              <Textarea
                value={jobDescNotes}
                onChange={(e) => setJobDescNotes(e.target.value)}
                placeholder="Optional notes for the AI, e.g. 'we need extra coverage in Kent'"
                rows={2}
                data-testid="product-job-description-notes"
              />
              <p className="text-[11px] text-muted-foreground">
                {editing?.product_id
                  ? "Reads this product's name, description and category to write a \"Become a Preferred Supplier\" pitch for it."
                  : "Save the product first — the AI reads its saved details to write a supplier pitch specific to it."}
              </p>
              <div className="[&_.ProseMirror]:min-h-[100px] [&_.ProseMirror]:max-h-[200px] [&_.ProseMirror]:overflow-y-auto">
                <TiptapEditor
                  value={form.job_description}
                  onChange={(html) => setForm({ ...form, job_description: html })}
                  placeholder="Click Generate with AI, or write your own supplier pitch here…"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-border p-3 bg-muted/10">
              <div className="sm:col-span-2 text-sm font-semibold -mb-1">Pricing</div>
              <p className="sm:col-span-2 text-xs text-muted-foreground -mt-1">
                Set any/all 3 — leave a price at 0 to not offer that option (one-off at £0 is a free product).
                Shoppers pick the billing type via a toggle on the product card.
              </p>
              <div>
                <Label>One-off price</Label>
                <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0 = not offered" data-testid="product-price" />
              </div>
              <div>
                <Label>Monthly price</Label>
                <Input type="number" step="0.01" min="0" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} placeholder="0 = not offered" data-testid="product-price-monthly" />
              </div>
              <div>
                <Label>Annual price</Label>
                <Input type="number" step="0.01" min="0" value={form.price_annual} onChange={(e) => setForm({ ...form, price_annual: e.target.value })} placeholder="0 = not offered" data-testid="product-price-annual" />
              </div>
              <div>
                <Label>Was price <span className="text-xs text-muted-foreground">(one-off only)</span></Label>
                <Input type="number" step="0.01" min="0" value={form.was_price} onChange={(e) => setForm({ ...form, was_price: e.target.value })} placeholder="0 = hide" data-testid="product-was-price" />
              </div>
              <div>
                <Label>Currency</Label>
                <select className="w-full h-10 px-3 border border-input rounded" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  {["GBP","USD","EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Expires in <span className="text-xs text-muted-foreground">(months after purchase)</span></Label>
                <Input type="number" step="1" min="0" value={form.expires_in_months} onChange={(e) => setForm({ ...form, expires_in_months: e.target.value })} placeholder="0 = never" data-testid="product-expires-in-months" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-end pb-2 gap-2">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-featured" />
                <Label className="mb-0 flex items-center gap-1"><Star className="h-3 w-3" /> Featured (pinned)</Label>
              </div>
              <div className="flex items-end pb-2 gap-2">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="product-enabled" />
                <Label className="mb-0">Live on shop</Label>
              </div>
            </div>
            <div className="flex items-end pb-2 gap-2">
              <Switch
                checked={form.checkout_on_add === true}
                onCheckedChange={(v) => setForm({ ...form, checkout_on_add: v })}
                data-testid="product-checkout-on-add"
              />
              <Label className="mb-0">Proceed straight to checkout once added to basket (skips the basket drawer)</Label>
            </div>
            <div className="flex items-end pb-2 gap-2">
              <Switch
                checked={form.allow_add_to_basket !== false}
                onCheckedChange={(v) => setForm({ ...form, allow_add_to_basket: v })}
                data-testid="product-allow-add-to-basket"
              />
              <Label className="mb-0">Show "Add to basket" CTA for this product</Label>
            </div>
            <div>
              <Label>Supplier</Label>
              <select
                className="w-full h-10 px-3 border border-input rounded bg-background"
                value={form.supplier_id || ""}
                onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                data-testid="product-supplier"
              >
                <option value="">Company (default)</option>
                {suppliers.filter((s) => s.enabled !== false).map((s) => (
                  <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">The company supplying this product. Leave as &quot;Company&quot; if you stock it yourself.</p>
            </div>
            <div>
              <Label>Categories (up to 3)</Label>
              <div className="border border-input rounded bg-background p-2 space-y-1 max-h-40 overflow-y-auto" data-testid="product-categories">
                {categories.length === 0 && (
                  <p className="text-xs text-muted-foreground px-1 py-1">No categories yet — add one in the Categories tab.</p>
                )}
                {categories.map((c) => {
                  const checked = (form.category_ids || []).includes(c.category_id);
                  const atLimit = (form.category_ids || []).length >= 3;
                  return (
                    <label
                      key={c.category_id}
                      className={`flex items-center gap-2 text-sm px-1 py-1 rounded ${!checked && atLimit ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-muted"}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!checked && atLimit}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...(form.category_ids || []), c.category_id]
                            : (form.category_ids || []).filter((id) => id !== c.category_id);
                          setForm({ ...form, category_ids: next });
                        }}
                        data-testid={`product-category-checkbox-${c.category_id}`}
                      />
                      {c.name}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                A product shows up under every category it's checked for. Leave all unchecked for Uncategorised. Manage categories in the <strong>Categories</strong> tab.
              </p>
            </div>
            <div>
              <Label>List order</Label>
              <select
                className="w-full h-10 px-3 border border-input rounded bg-background"
                value={form.list_order || "medium"}
                onChange={(e) => setForm({ ...form, list_order: e.target.value })}
                data-testid="product-list-order"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Drives the order on the shop grid — High first, then Medium, then Low.</p>
            </div>
            <div>
              <Label>Image</Label>
              <div className="flex gap-2 items-center mt-1">
                <label className="inline-flex items-center gap-1.5 px-3 py-2 border border-input rounded text-xs cursor-pointer hover:bg-muted transition-colors" data-testid="product-image-upload-btn">
                  {uploadingImage ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  {uploadingImage ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageFile} disabled={uploadingImage} data-testid="product-image-file" />
                </label>
                <span className="text-xs text-muted-foreground">or paste URL</span>
              </div>
              <Input
                value={form.image_url}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.trim().startsWith("data:")) {
                    toast.error("Pasted image data won't display in emails or on some pages — please use \"Upload image\" instead.");
                  }
                  setForm({ ...form, image_url: v });
                }}
                placeholder="https://…"
                className="mt-2"
                data-testid="product-image"
              />
              {form.image_url?.trim().startsWith("data:") && (
                <p className="text-xs text-amber-600 mt-1" data-testid="product-image-base64-warning">
                  This is pasted image data, not a real link — it won&apos;t show up in order confirmation emails. Use &quot;Upload image&quot; instead.
                </p>
              )}
              {form.image_url && (
                <div
                  className="mt-2 relative h-24 w-24 rounded border overflow-hidden bg-muted"
                  data-testid="product-image-preview"
                >
                  <img src={form.image_url} alt="" className="h-full w-full object-cover" />
                  {form.badge_text && (
                    <ProductBadgePreview
                      text={form.badge_text}
                      position={form.badge_position}
                      bgColor={form.badge_bg_color}
                      textColor={form.badge_text_color}
                      fontFamily={form.badge_font_family}
                      fontSize={form.badge_font_size}
                      rotation={form.badge_rotation}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Badge overlay — optional text chip painted on the image.
                Auto-sizes when badge_font_size = 0 (scales from the
                rendered image width). Position + colours + rotation +
                font all admin-configurable so campaigns like SALE / NEW
                / -20% render without touching the image file itself. */}
            <div className="border border-border rounded p-3 space-y-3 bg-muted/20">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">Image badge overlay</div>
                <span className="text-xs text-muted-foreground">Leave text empty to hide</span>
              </div>
              <div>
                <Label>Badge text</Label>
                <Input
                  value={form.badge_text}
                  onChange={(e) => setForm({ ...form, badge_text: e.target.value })}
                  placeholder="SALE, NEW, -25%…"
                  maxLength={24}
                  data-testid="product-badge-text"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Location</Label>
                  <select
                    className="w-full h-9 mt-1 border border-input bg-background rounded px-2 text-sm"
                    value={form.badge_position || "top-right"}
                    onChange={(e) => setForm({ ...form, badge_position: e.target.value })}
                    data-testid="product-badge-position"
                  >
                    <option value="top-left">Top left</option>
                    <option value="top-right">Top right</option>
                    <option value="bottom-left">Bottom left</option>
                    <option value="bottom-right">Bottom right</option>
                    <option value="center">Center</option>
                  </select>
                </div>
                <div>
                  <Label>Font family</Label>
                  <select
                    className="w-full h-9 mt-1 border border-input bg-background rounded px-2 text-sm"
                    value={form.badge_font_family || ""}
                    onChange={(e) => setForm({ ...form, badge_font_family: e.target.value })}
                    data-testid="product-badge-font"
                  >
                    <option value="">Site default</option>
                    <option value="Inter, sans-serif">Inter</option>
                    <option value="'Playfair Display', serif">Playfair Display</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Courier New', monospace">Courier</option>
                    <option value="Impact, 'Arial Black', sans-serif">Impact</option>
                  </select>
                </div>
                <div>
                  <Label>Background</Label>
                  <input
                    type="color"
                    value={form.badge_bg_color || "#ef4444"}
                    onChange={(e) => setForm({ ...form, badge_bg_color: e.target.value })}
                    className="mt-1 h-9 w-full rounded border border-input cursor-pointer"
                    data-testid="product-badge-bg"
                  />
                </div>
                <div>
                  <Label>Foreground</Label>
                  <input
                    type="color"
                    value={form.badge_text_color || "#ffffff"}
                    onChange={(e) => setForm({ ...form, badge_text_color: e.target.value })}
                    className="mt-1 h-9 w-full rounded border border-input cursor-pointer"
                    data-testid="product-badge-fg"
                  />
                </div>
                <div>
                  <Label>Font size (0 = auto)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="200"
                    step="1"
                    value={form.badge_font_size ?? 0}
                    onChange={(e) => setForm({ ...form, badge_font_size: parseInt(e.target.value) || 0 })}
                    data-testid="product-badge-size"
                  />
                </div>
                <div>
                  <Label>Rotation (°)</Label>
                  <Input
                    type="number"
                    min="-180"
                    max="180"
                    step="1"
                    value={form.badge_rotation ?? 0}
                    onChange={(e) => setForm({ ...form, badge_rotation: parseInt(e.target.value) || 0 })}
                    data-testid="product-badge-rotation"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <Label>Image height (px)</Label>
                <Input
                  type="number"
                  min="60"
                  max="800"
                  step="10"
                  value={form.image_height || 260}
                  onChange={(e) => setForm({ ...form, image_height: parseInt(e.target.value) || 0 })}
                  data-testid="product-image-height"
                />
                <p className="text-xs text-muted-foreground mt-1">Height of the image slot on the shop grid. The image is never cropped by default.</p>
              </div>
              <div>
                <Label className="whitespace-nowrap">Fit</Label>
                <div className="inline-flex border border-border rounded overflow-hidden text-sm mt-1" role="radiogroup" data-testid="product-image-fit">
                  {[
                    { v: "contain", label: "No crop" },
                    { v: "cover",   label: "Crop-fill" },
                  ].map((opt) => {
                    const active = (form.image_fit || "contain") === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setForm({ ...form, image_fit: opt.v })}
                        className={`px-3 py-1.5 font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                        title={opt.v === "contain" ? "Whole image visible, letterboxed if needed" : "Image fills the slot, edges may be cropped"}
                        data-testid={`product-image-fit-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={async () => { await autosave.saveNow(); setShowDialog(false); fetchAll(); }}
              data-testid="product-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditOrderDialog
        open={!!editingOrder}
        onOpenChange={(v) => !v && setEditingOrder(null)}
        order={editingOrder}
        apiBase="/admin/orders"
        onSaved={() => fetchAll({ silent: true })}
        onDeleted={() => { setEditingOrder(null); fetchAll({ silent: true }); }}
      />
    </div>
  );
}
